import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CopyIcon, CopyPlusIcon, DownloadIcon } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../ui/PageHeader';
import { MissingItem } from '../ui/MissingItem';
import { Panel } from '../ui/Panel';
import { Field } from '../ui/Field';
import { CheckList } from '../ui/CheckList';
import { DeleteButton } from '../ui/DeleteButton';
import { undoToast } from '../ui/undoToast';
import { NewsletterBlocks } from './NewsletterBlocks';
import { NewsletterPreview } from './NewsletterPreview';
import { useAdmin } from '../AdminContext';
import { useNewsletters } from './useNewsletters';
import { useNewsletterContext } from './useNewsletterContext';
import { NewsletterSendPanel } from './NewsletterSendPanel';
import { useSegments } from '../mail/useSegments';
import { newsletterHtml } from './newsletterHtml';
import { PREHEADER_MAX, SUBJECT_MAX, isLocked, issueChecks, newsletterStatuses } from './newsletterRules';
import { fillTokens } from '../marketing/marketingRules';
import { errorCount, slugify } from '../editing';
import { btnGhost, btnSecondary, inputCls } from '../ui/styles';
import { fromIso, longDate, today } from '../../core/format/dates';
import { downloadFile } from '../../utils/download';
import type { NewsletterIssue } from '../../core/types/newsletter';
const back = {
  to: '/be/mail?tab=nieuwsbrief',
  label: 'Nieuwsbrief'
};

/** Eén nieuwsbrief samenstellen: onderwerp, blokken en planning, met live voorbeeld zoals de lezer hem krijgt. */
export function NewsletterEditor() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    site
  } = useAdmin();
  const {
    issues,
    updateIssue,
    removeIssue,
    duplicateIssue
  } = useNewsletters();
  const segments = useSegments();
  const ctx = useNewsletterContext();
  const issue = issues.find(n => n.id === id);
  const html = useMemo(() => issue ? newsletterHtml(issue, ctx) : '', [issue, ctx]);
  if (!issue) {
    return <MissingItem title="Nieuwsbrief niet gevonden" back={back} data-id="element-3360" data-mp="4hmo66ix7pix" />;
  }
  const set = (patch: Partial<NewsletterIssue>) => updateIssue(issue.id, patch);
  const checks = issueChecks(issue, ctx);
  const errors = errorCount(checks);
  const locked = isLocked(issue);
  const subject = fillTokens(issue.subject, ctx.tokens);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(html);
      toast.success('HTML gekopieerd', {
        description: 'Plak hem in Brevo bij een nieuwe campagne, onder “Plak je eigen code”. Verstuurd? Zet hier dan de status op Verstuurd.'
      });
    } catch {
      toast.error('Kopiëren lukte niet', {
        description: 'Download het bestand en upload dat in Brevo.'
      });
    }
  };
  const download = () => downloadFile(html, `nieuwsbrief-${slugify(issue.subject) || issue.id}.html`, 'text/html;charset=utf-8');
  const setStatus = (status: NewsletterIssue['status']) => set(status === 'verstuurd' && !issue.sendDate ? {
    status,
    sendDate: today()
  } : {
    status
  });
  return <div data-id="element-3361" data-mp="2wbxn48wy9l0p">
      <PageHeader title={subject || 'Nieuwe nieuwsbrief'} description={issue.sendDate ? `Gepland op ${longDate(fromIso(issue.sendDate))}` : 'Nog zonder verzenddag'} back={back} actions={<>
            <button type="button" onClick={() => duplicateIssue(issue)} className={btnSecondary} data-id="element-3363" data-mp="2mkt8jab6s26e">
              <CopyPlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3364" data-mp="2hm8jm71dx4x9" />
              Dupliceer
            </button>
            <button type="button" onClick={copy} disabled={errors > 0} aria-describedby={errors ? 'nieuwsbrief-blokkade' : undefined} className={btnSecondary} data-id="element-3365" data-mp="28mv9dc5fscbh">
              <CopyIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3366" data-mp="1yqry9gslx3ce" />
              Kopieer voor Brevo
            </button>
          </>} data-id="element-3362" data-mp="2wbnwcj42z9ty" />
      {errors > 0 && <p id="nieuwsbrief-blokkade" className="-mt-3 mb-6 text-[13px] text-muted sm:-mt-5" data-id="element-3367" data-mp="3t4jamo2bm1bj">
          Nog {errors === 1 ? '1 punt' : `${errors} punten`} op te lossen voordat hij de deur uit kan; zie “Klaar om te versturen?”.
        </p>}
      {locked && <p role="status" className="mb-6 rounded-md bg-sand px-4 py-3 text-[13px] text-ink" data-id="element-3368" data-mp="2gs5roox2ubf9">
          {issue.delivery?.status === 'verzonden' ? 'Deze nieuwsbrief is verstuurd en kan niet meer veranderen. Dupliceer hem om er een nieuwe van te maken.' : 'Ingepland: de inhoud staat vast. Annuleer het inplannen om nog iets aan te passen of hem te verwijderen.'}
        </p>}

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-3369" data-mp="1y1xqwdgunx28">
        {/* Ingepland of verzonden: de server weigert wijzigen (409), dus hier ook alles vast. */}
        <fieldset disabled={locked} className="min-w-0 space-y-6 disabled:opacity-70 lg:col-span-7" data-id="element-3370" data-mp="22y4zyf4jlooe">
          <legend className="sr-only" data-id="element-3371" data-mp="1o51q6tx70f88">Inhoud van de nieuwsbrief</legend>
          <Panel title="In de inbox" data-id="element-3372" data-mp="1t0qmmodx1g0b">
            <div className="space-y-5" data-id="element-3373" data-mp="1t3wci5udclx3">
              <Field label="Onderwerp" count={{
              value: subject.length,
              max: SUBJECT_MAX
            }} hint="Persoonlijk en concreet werkt beter dan een aanbieding." data-id="element-3374" data-mp="19hb69x01c37g">
                <input value={issue.subject} onChange={e => set({
                subject: e.target.value
              })} className={inputCls} data-id="element-3375" data-mp="15h0yqfwlh4qh" />
              </Field>
              <Field label="Voorvertoningsregel" count={{
              value: issue.preheader.length,
              max: PREHEADER_MAX
            }} hint="De grijze regel achter het onderwerp. Maak de zin van het onderwerp af." data-id="element-3376" data-mp="1ef36deau2vmq">
                <input value={issue.preheader} onChange={e => set({
                preheader: e.target.value
              })} className={inputCls} data-id="element-3377" data-mp="1obprm472nopa" />
              </Field>
            </div>
          </Panel>

          <Panel title="Inhoud" description="Van boven naar beneden. Onderaan komen vanzelf je adres en de afmeldlink." data-id="element-3378" data-mp="1x6m9rm8rsbpa">
            <NewsletterBlocks blocks={issue.blocks} onChange={blocks => set({
            blocks
          })} data-id="element-3379" data-mp="2c0qxpksfmnre" />
          </Panel>

          <Panel title="Planning" data-id="element-3380" data-mp="2e7i3x7pkvlfl">
            <div className="space-y-5" data-id="element-3381" data-mp="2e89ntl9tbuq1">
              {!issue.delivery && <Field label="Status" as="div" hint="Voor als je hem zelf via Brevo verstuurt." data-id="element-3382" data-mp="2j5s11nuv4w3u">

                <div role="radiogroup" aria-label="Status" className="grid gap-1.5 sm:grid-cols-3" data-id="element-3383" data-mp="2o4w92b61wnsl">
                  {newsletterStatuses.map(s => <button key={s.key} type="button" role="radio" aria-checked={issue.status === s.key} onClick={() => setStatus(s.key)} className={`rounded-md px-3 py-2.5 text-left transition-colors duration-150 ease-soft ${issue.status === s.key ? 'bg-ink text-shell' : 'bg-white text-ink ring-1 ring-line hover:ring-ink/30'}`} data-id="element-3384" data-mp="2sxfo7ckdzqzi">
                      <span className="block text-[13px]" data-id="element-3385" data-mp="1hgpli3f69han">{s.label}</span>
                      <span className={`block text-[12px] leading-snug ${issue.status === s.key ? 'text-shell/80' : 'text-muted'}`} data-id="element-3386" data-mp="1hj248isr7vor">{s.text}</span>
                    </button>)}
                </div>
              </Field>}
              <Field label="Verzenddag" hint="Plan in Brevo op dezelfde dag. Dinsdag- of donderdagochtend wordt het best gelezen." data-id="element-3387" data-mp="108opmhkdjqi2">
                <input type="date" value={issue.sendDate} onChange={e => set({
                sendDate: e.target.value
              })} className={`${inputCls} sm:max-w-[220px]`} data-id="element-3388" data-mp="vade82efl7xq" />
              </Field>
              <Field label="Notities" hint="Alleen voor jou." data-id="element-3389" data-mp="1nyizcq9ecyy3">
                <textarea value={issue.notes} onChange={e => set({
                notes: e.target.value
              })} rows={3} className={`${inputCls} resize-y leading-relaxed`} data-id="element-3390" data-mp="1ixv2vp2efpwh" />
              </Field>
            </div>
            <div className="mt-6 border-t border-line pt-4" data-id="element-3391" data-mp="7ipx2c6z1b8n">
              <DeleteButton undoable label="Nieuwsbrief verwijderen" onConfirm={() => {
              const restore = removeIssue(issue.id);
              navigate(back.to);
              undoToast(`“${subject || 'Nieuwe nieuwsbrief'}” verwijderd`, restore, {
                description: 'Pas definitief als je opslaat.',
                restored: 'Nieuwsbrief teruggezet'
              });
            }} data-id="element-3392" data-mp="7k991qllh04m" />
            </div>
          </Panel>
        </fieldset>

        <div className="min-w-0 lg:col-span-5" data-id="element-3393" data-mp="1ay05zu1vwnob">
          <div className="space-y-6 lg:sticky lg:top-20" data-id="element-3394" data-mp="1aza0yadfxfqq">
            <Panel title="Klaar om te versturen?" data-id="element-3395" data-mp="1fxuwvxlntd9d">
              <CheckList checks={checks} okText="Alles staat klaar." data-id="element-3396" data-mp="1fzfp3gki4k42" />
            </Panel>
            <NewsletterSendPanel issue={issue} errors={errors} segments={segments.list?.items ?? null} totalReaders={segments.list?.total ?? null} data-id="element-3397" data-mp="1243vxdlzv1p9" />
            <NewsletterPreview html={html} subject={subject} preheader={fillTokens(issue.preheader, ctx.tokens)} from={`${site.founder.firstName} van ${site.salon.name}`} data-id="element-3398" data-mp="1tx48ejyx7j70" />
            <div className="flex justify-center" data-id="element-3399" data-mp="3jcorwn9cf5bb">
              <button type="button" onClick={download} className={btnGhost} data-id="element-3400" data-mp="3efyc811gabqf">
                <DownloadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3401" data-mp="3t4b7evsprqjv" />
                Download als HTML-bestand
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}